'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Circle, Square, Play, Pause, SkipBack, SkipForward,
  Bookmark, Trash2, Clock, ChevronDown, ChevronUp,
  Brain, Zap, Target, AlertCircle
} from 'lucide-react';
import { useSession } from './SessionProvider';
import { eegSessionService } from '@/lib/services/eegSessionService';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TimestampMarker {
  id: string;
  time: number;
  label: string;
  metrics: SnapshotMetrics;
}

interface SnapshotMetrics {
  cognitiveLoad: number;
  attentionScore: number;
  betaPower: number;
  alphaPower: number;
  thetaPower: number;
  thetaAlphaRatio: number;
  interHemisphericCoherence: number;
}

interface RecordedSession {
  id: string;
  startedAt: Date;
  duration: number;
  markers: TimestampMarker[];
  snapshots: Array<{ time: number; metrics: SnapshotMetrics }>;
  aiInsight?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function generateSessionId(): string {
  return `REC-${Date.now().toString(36).toUpperCase()}`;
}

function randomMetrics(): SnapshotMetrics {
  return {
    cognitiveLoad: 40 + Math.random() * 50,
    attentionScore: 35 + Math.random() * 55,
    betaPower: 8 + Math.random() * 18,
    alphaPower: 6 + Math.random() * 14,
    thetaPower: 4 + Math.random() * 10,
    thetaAlphaRatio: 0.7 + Math.random() * 1.0,
    interHemisphericCoherence: 50 + Math.random() * 40,
  };
}

// ─── Metric Pill ──────────────────────────────────────────────────────────────
function MetricPill({ label, value, unit, color }: { label: string; value: number; unit: string; color: string }) {
  return (
    <div className="flex flex-col items-center px-3 py-2 rounded-lg bg-[#0B1628]/80 border border-[#1E3A5F]/50 min-w-[72px]">
      <span className="text-[10px] text-zinc-500 mb-0.5">{label}</span>
      <span className="text-[13px] font-mono font-bold tabular-nums" style={{ color }}>
        {value.toFixed(value < 10 ? 1 : 0)}
        <span className="text-[9px] font-normal text-zinc-500 ml-0.5">{unit}</span>
      </span>
    </div>
  );
}

// ─── Playback Timeline ────────────────────────────────────────────────────────
function PlaybackTimeline({
  duration,
  currentTime,
  markers,
  onSeek,
}: {
  duration: number;
  currentTime: number;
  markers: TimestampMarker[];
  onSeek: (t: number) => void;
}) {
  const barRef = useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!barRef.current || duration === 0) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(ratio * duration);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full select-none">
      <div
        ref={barRef}
        onClick={handleClick}
        className="relative h-2 rounded-full bg-[#1E3A5F]/60 cursor-pointer group overflow-visible"
        style={{ boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)' }}
      >
        {/* Progress fill */}
        <div
          className="absolute left-0 top-0 h-full rounded-full transition-all duration-100"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #F97316, #FF9A4D)',
            boxShadow: '0 0 8px rgba(249,115,22,0.5)',
          }}
        />
        {/* Playhead */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-[#F97316] border-2 border-white shadow-lg transition-all duration-100 opacity-0 group-hover:opacity-100"
          style={{ left: `calc(${progress}% - 7px)`, boxShadow: '0 0 10px rgba(249,115,22,0.8)' }}
        />
        {/* Timestamp markers */}
        {markers.map((m) => {
          const pos = duration > 0 ? (m.time / duration) * 100 : 0;
          return (
            <div
              key={m.id}
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-amber-400 border border-amber-200 cursor-pointer hover:scale-150 transition-transform z-10"
              style={{ left: `calc(${pos}% - 4px)`, boxShadow: '0 0 6px rgba(251,191,36,0.8)' }}
              title={`${m.label} @ ${formatTime(m.time)}`}
              onClick={(e) => { e.stopPropagation(); onSeek(m.time); }}
            />
          );
        })}
      </div>
      {/* Time labels */}
      <div className="flex justify-between mt-1">
        <span className="text-[10px] font-mono text-zinc-600">{formatTime(currentTime)}</span>
        <span className="text-[10px] font-mono text-zinc-600">{formatTime(duration)}</span>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SessionRecordingControls() {
  const { metrics } = useSession();

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [markers, setMarkers] = useState<TimestampMarker[]>([]);
  const [snapshots, setSnapshots] = useState<Array<{ time: number; metrics: SnapshotMetrics }>>([]);
  const [recordingStartedAt, setRecordingStartedAt] = useState<Date | null>(null);
  const [currentRecordingId, setCurrentRecordingId] = useState<string>('');

  // Saved sessions
  const [savedSessions, setSavedSessions] = useState<RecordedSession[]>([]);

  // Playback state
  const [playbackSession, setPlaybackSession] = useState<RecordedSession | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [playbackMetrics, setPlaybackMetrics] = useState<SnapshotMetrics | null>(null);
  const [activeMarker, setActiveMarker] = useState<TimestampMarker | null>(null);

  // UI state
  const [showSessions, setShowSessions] = useState(false);
  const [markerLabel, setMarkerLabel] = useState('');
  const [showMarkerInput, setShowMarkerInput] = useState(false);
  const [aiInsight, setAiInsight] = useState<string>('');
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [savingToSupabase, setSavingToSupabase] = useState(false);
  const [lastSavedSessionId, setLastSavedSessionId] = useState<string | null>(null);

  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playbackTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const snapshotTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingTimeRef = useRef(0);
  const metricsRef = useRef(metrics);

  // Keep metricsRef in sync
  useEffect(() => { metricsRef.current = metrics; }, [metrics]);

  // ── Recording ──────────────────────────────────────────────────────────────
  const startRecording = useCallback(() => {
    const id = generateSessionId();
    setCurrentRecordingId(id);
    setRecordingTime(0);
    recordingTimeRef.current = 0;
    setMarkers([]);
    setSnapshots([]);
    setRecordingStartedAt(new Date());
    setIsRecording(true);
  }, []);

  const stopRecording = useCallback(() => {
    setIsRecording(false);
    setMarkers((currentMarkers) => {
      setSnapshots((currentSnapshots) => {
        const session: RecordedSession = {
          id: currentRecordingId,
          startedAt: recordingStartedAt || new Date(),
          duration: recordingTimeRef.current,
          markers: currentMarkers,
          snapshots: currentSnapshots,
        };
        setSavedSessions((prev) => [session, ...prev]);

        // ── Persist to Supabase ──────────────────────────────────────────────
        setSavingToSupabase(true);
        eegSessionService.saveSession({
          sessionKey: currentRecordingId,
          subjectId: 'anonymous',
          startedAt: recordingStartedAt || new Date(),
          endedAt: new Date(),
          durationSeconds: recordingTimeRef.current,
          snapshots: currentSnapshots.map((s) => ({
            time: s.time,
            metrics: {
              cognitiveLoad: s.metrics.cognitiveLoad,
              attentionScore: s.metrics.attentionScore,
              betaPower: s.metrics.betaPower,
              alphaPower: s.metrics.alphaPower,
              thetaPower: s.metrics.thetaPower,
              thetaAlphaRatio: s.metrics.thetaAlphaRatio,
              interHemisphericCoherence: s.metrics.interHemisphericCoherence,
            },
          })),
          markerCount: currentMarkers.length,
        }).then((sessionId) => {
          if (sessionId) {
            setLastSavedSessionId(sessionId);
            // Dispatch event so SessionHistoryPanel auto-refreshes
            window.dispatchEvent(new CustomEvent('session:saved', { detail: { sessionId } }));
          }
        }).catch((err) => {
          console.warn('Failed to persist session to Supabase:', err);
        }).finally(() => {
          setSavingToSupabase(false);
        });

        return currentSnapshots;
      });
      return currentMarkers;
    });
    setShowSessions(true);
  }, [currentRecordingId, recordingStartedAt]);

  // Recording timer
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        recordingTimeRef.current += 1;
        setRecordingTime((t) => t + 1);
      }, 1000);
    } else {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    }
    return () => { if (recordingTimerRef.current) clearInterval(recordingTimerRef.current); };
  }, [isRecording]);

  // Snapshot every 5s during recording
  useEffect(() => {
    if (isRecording) {
      snapshotTimerRef.current = setInterval(() => {
        const m = metricsRef.current;
        const snap: SnapshotMetrics = {
          cognitiveLoad: m.cognitiveLoadIndex,
          attentionScore: m.attentionScore,
          betaPower: m.betaPower,
          alphaPower: m.alphaPower,
          thetaPower: m.thetaPower,
          thetaAlphaRatio: m.thetaAlphaRatio,
          interHemisphericCoherence: m.interHemisphericCoherence,
        };
        setSnapshots((prev) => [...prev, { time: recordingTimeRef.current, metrics: snap }]);
      }, 5000);
    } else {
      if (snapshotTimerRef.current) clearInterval(snapshotTimerRef.current);
    }
    return () => { if (snapshotTimerRef.current) clearInterval(snapshotTimerRef.current); };
  }, [isRecording]);

  // ── Timestamp Markers ──────────────────────────────────────────────────────
  const addMarker = useCallback(() => {
    if (!isRecording) return;
    const label = markerLabel.trim() || `Marker ${markers.length + 1}`;
    const m = metricsRef.current;
    const marker: TimestampMarker = {
      id: `m-${Date.now()}`,
      time: recordingTimeRef.current,
      label,
      metrics: {
        cognitiveLoad: m.cognitiveLoadIndex,
        attentionScore: m.attentionScore,
        betaPower: m.betaPower,
        alphaPower: m.alphaPower,
        thetaPower: m.thetaPower,
        thetaAlphaRatio: m.thetaAlphaRatio,
        interHemisphericCoherence: m.interHemisphericCoherence,
      },
    };
    setMarkers((prev) => [...prev, marker]);
    setMarkerLabel('');
    setShowMarkerInput(false);
  }, [isRecording, markerLabel, markers.length]);

  // ── Playback ───────────────────────────────────────────────────────────────
  const loadSession = useCallback((session: RecordedSession) => {
    setPlaybackSession(session);
    setPlaybackTime(0);
    setIsPlaying(false);
    setActiveMarker(null);
    setAiInsight(session.aiInsight || '');
    const first = session.snapshots[0];
    setPlaybackMetrics(first ? first.metrics : randomMetrics());
  }, []);

  const seekPlayback = useCallback((t: number) => {
    if (!playbackSession) return;
    setPlaybackTime(t);
    const snaps = playbackSession.snapshots;
    if (snaps.length > 0) {
      const nearest = snaps.reduce((a, b) => Math.abs(b.time - t) < Math.abs(a.time - t) ? b : a);
      setPlaybackMetrics(nearest.metrics);
    }
    const nearMarker = playbackSession.markers.find((m) => Math.abs(m.time - t) < 3);
    setActiveMarker(nearMarker || null);
  }, [playbackSession]);

  const skipToMarker = useCallback((direction: 'prev' | 'next') => {
    if (!playbackSession) return;
    const sorted = [...playbackSession.markers].sort((a, b) => a.time - b.time);
    if (direction === 'next') {
      const next = sorted.find((m) => m.time > playbackTime + 0.5);
      if (next) seekPlayback(next.time);
    } else {
      const prev = [...sorted].reverse().find((m) => m.time < playbackTime - 0.5);
      if (prev) seekPlayback(prev.time);
    }
  }, [playbackSession, playbackTime, seekPlayback]);

  // Playback timer
  useEffect(() => {
    if (isPlaying && playbackSession) {
      playbackTimerRef.current = setInterval(() => {
        setPlaybackTime((t) => {
          const next = t + 0.25;
          if (next >= playbackSession.duration) {
            setIsPlaying(false);
            return playbackSession.duration;
          }
          const snaps = playbackSession.snapshots;
          if (snaps.length > 0) {
            const nearest = snaps.reduce((a, b) => Math.abs(b.time - next) < Math.abs(a.time - next) ? b : a);
            setPlaybackMetrics(nearest.metrics);
          }
          const nearMarker = playbackSession.markers.find((m) => Math.abs(m.time - next) < 2);
          setActiveMarker(nearMarker || null);
          return next;
        });
      }, 250);
    } else {
      if (playbackTimerRef.current) clearInterval(playbackTimerRef.current);
    }
    return () => { if (playbackTimerRef.current) clearInterval(playbackTimerRef.current); };
  }, [isPlaying, playbackSession]);

  // ── AI Insight ─────────────────────────────────────────────────────────────
  const generateInsight = useCallback(async (session: RecordedSession) => {
    setLoadingInsight(true);
    try {
      const snaps = session.snapshots;
      const avgMetrics: SnapshotMetrics = snaps.length > 0
        ? snaps.reduce(
            (acc, s) => ({
              cognitiveLoad: acc.cognitiveLoad + s.metrics.cognitiveLoad / snaps.length,
              attentionScore: acc.attentionScore + s.metrics.attentionScore / snaps.length,
              betaPower: acc.betaPower + s.metrics.betaPower / snaps.length,
              alphaPower: acc.alphaPower + s.metrics.alphaPower / snaps.length,
              thetaPower: acc.thetaPower + s.metrics.thetaPower / snaps.length,
              thetaAlphaRatio: acc.thetaAlphaRatio + s.metrics.thetaAlphaRatio / snaps.length,
              interHemisphericCoherence: acc.interHemisphericCoherence + s.metrics.interHemisphericCoherence / snaps.length,
            }),
            { cognitiveLoad: 0, attentionScore: 0, betaPower: 0, alphaPower: 0, thetaPower: 0, thetaAlphaRatio: 0, interHemisphericCoherence: 0 }
          )
        : randomMetrics();

      const res = await fetch('/api/ai/chat-completion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            {
              role: 'user',
              content: `Summarize this archived EEG cognitive session in 2–3 concise sentences. Session duration: ${formatTime(session.duration)}. Markers: ${session.markers.length}. Avg metrics — Cognitive Load: ${avgMetrics.cognitiveLoad.toFixed(0)}/100, Attention: ${avgMetrics.attentionScore.toFixed(0)}/100, Beta: ${avgMetrics.betaPower.toFixed(1)} µV², Alpha: ${avgMetrics.alphaPower.toFixed(1)} µV², Theta/Alpha ratio: ${avgMetrics.thetaAlphaRatio.toFixed(2)}, Coherence: ${avgMetrics.interHemisphericCoherence.toFixed(0)}%. Provide key cognitive state summary and one actionable tip.`,
            },
          ],
          systemPrompt: 'You are a neuroscience expert. Provide concise, insightful EEG session summaries in plain text (no markdown). Keep it under 80 words.',
        }),
      });
      const data = await res.json();
      const insight = data?.content || data?.message || 'Session analysis complete.';
      setAiInsight(insight);
      setSavedSessions((prev) =>
        prev.map((s) => s.id === session.id ? { ...s, aiInsight: insight } : s)
      );
      setPlaybackSession((prev) => prev && prev.id === session.id ? { ...prev, aiInsight: insight } : prev);
    } catch {
      setAiInsight('Unable to generate AI insight for this session.');
    } finally {
      setLoadingInsight(false);
    }
  }, []);

  const deleteSession = useCallback((id: string) => {
    setSavedSessions((prev) => prev.filter((s) => s.id !== id));
    if (playbackSession?.id === id) {
      setPlaybackSession(null);
      setIsPlaying(false);
    }
  }, [playbackSession]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* ── Recording Controls Bar ── */}
      <div
        className="rounded-xl border border-[#1E3A5F]/70 bg-[#0B1628]/90 backdrop-blur-sm overflow-hidden"
        style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(249,115,22,0.06)' }}
      >
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#F97316]/50 to-transparent" />

        <div className="px-4 py-3 flex flex-wrap items-center gap-3">
          {/* Record / Stop button */}
          {!isRecording ? (
            <button
              onClick={startRecording}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold transition-all duration-150 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(249,115,22,0.08))',
                border: '1px solid rgba(249,115,22,0.4)',
                color: '#F97316',
                boxShadow: '0 0 12px rgba(249,115,22,0.15)',
              }}
            >
              <Circle size={14} className="fill-[#F97316] text-[#F97316]" />
              Start Recording
            </button>
          ) : (
            <button
              onClick={stopRecording}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold transition-all duration-150 active:scale-95"
              style={{
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.4)',
                color: '#f87171',
                boxShadow: '0 0 12px rgba(239,68,68,0.15)',
              }}
            >
              <Square size={13} className="fill-red-400 text-red-400" />
              Stop Recording
            </button>
          )}

          {/* Recording indicator + timer */}
          {isRecording && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-950/30 border border-red-800/40">
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" style={{ boxShadow: '0 0 6px rgba(248,113,113,0.8)' }} />
              <span className="text-[12px] font-mono tabular-nums text-red-300 font-semibold">{formatTime(recordingTime)}</span>
              <span className="text-[10px] text-red-500 uppercase tracking-wider">REC</span>
            </div>
          )}

          {/* Marker button */}
          {isRecording && (
            <div className="flex items-center gap-2">
              {showMarkerInput ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={markerLabel}
                    onChange={(e) => setMarkerLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addMarker()}
                    placeholder="Marker label..."
                    autoFocus
                    className="px-2.5 py-1.5 rounded-md text-[12px] bg-[#0F1E35] border border-[#1E3A5F]/80 text-zinc-200 placeholder-zinc-600 outline-none focus:border-amber-500/50 w-36"
                  />
                  <button
                    onClick={addMarker}
                    className="px-3 py-1.5 rounded-md text-[12px] font-semibold bg-amber-500/15 border border-amber-500/40 text-amber-400 hover:bg-amber-500/25 transition-all active:scale-95"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setShowMarkerInput(false); setMarkerLabel(''); }}
                    className="px-2 py-1.5 rounded-md text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowMarkerInput(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-all active:scale-95"
                >
                  <Bookmark size={13} />
                  Mark Moment
                </button>
              )}
            </div>
          )}

          {/* Marker count badge */}
          {isRecording && markers.length > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#0F1E35] border border-[#1E3A5F]/50">
              <Bookmark size={11} className="text-amber-400" />
              <span className="text-[11px] font-mono text-amber-400">{markers.length} marker{markers.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          <div className="flex-1" />

          {/* Supabase save indicator */}
          {savingToSupabase && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-950/30 border border-emerald-800/40">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] text-emerald-400 font-medium">Saving to cloud…</span>
            </div>
          )}
          {lastSavedSessionId && !savingToSupabase && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-950/20 border border-emerald-800/30">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-[10px] text-emerald-400 font-medium">Saved to cloud</span>
            </div>
          )}

          {/* Sessions toggle */}
          <button
            onClick={() => setShowSessions((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-[#0F1E35] border border-[#1E3A5F]/60 text-zinc-400 hover:text-zinc-200 hover:border-[#F97316]/30 transition-all"
          >
            <Clock size={13} className="text-[#2D5A8E]" />
            <span>Sessions ({savedSessions.length})</span>
            {showSessions ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>

        {/* Markers list (during recording) */}
        {isRecording && markers.length > 0 && (
          <div className="px-4 pb-3 flex flex-wrap gap-2">
            {markers.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/25 text-amber-300 text-[11px]"
              >
                <Bookmark size={10} className="text-amber-400" />
                <span className="font-medium">{m.label}</span>
                <span className="text-amber-600 font-mono">{formatTime(m.time)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Saved Sessions List ── */}
      {showSessions && (
        <div
          className="rounded-xl border border-[#1E3A5F]/70 bg-[#0B1628]/90 backdrop-blur-sm overflow-hidden"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.4)' }}
        >
          <div className="px-4 py-3 border-b border-[#1E3A5F]/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-[#F97316]" />
              <span className="text-[13px] font-semibold text-zinc-200">Archived Sessions</span>
            </div>
            <span className="text-[11px] text-zinc-600">{savedSessions.length} session{savedSessions.length !== 1 ? 's' : ''}</span>
          </div>

          {savedSessions.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <AlertCircle size={24} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-[12px] text-zinc-600">No recorded sessions yet. Start recording to capture cognitive states.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#1E3A5F]/30 max-h-64 overflow-y-auto">
              {savedSessions.map((session) => (
                <div
                  key={session.id}
                  className={`px-4 py-3 flex items-center gap-3 hover:bg-[#0F1E35]/60 transition-colors cursor-pointer group ${playbackSession?.id === session.id ? 'bg-[#F97316]/5 border-l-2 border-[#F97316]' : ''}`}
                  onClick={() => loadSession(session)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[12px] font-mono font-semibold text-[#F97316]">{session.id}</span>
                      <span className="text-[10px] text-zinc-600 font-mono">{formatTime(session.duration)}</span>
                      {session.markers.length > 0 && (
                        <span className="flex items-center gap-1 text-[10px] text-amber-500">
                          <Bookmark size={9} />
                          {session.markers.length}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-zinc-600">
                      {session.startedAt.toLocaleTimeString('en-US', { hour12: false })} · {session.snapshots.length} snapshots
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); loadSession(session); generateInsight(session); }}
                      className="p-1.5 rounded-md bg-[#F97316]/10 border border-[#F97316]/20 text-[#F97316] hover:bg-[#F97316]/20 transition-all"
                      title="Generate AI insight"
                    >
                      <Brain size={12} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                      className="p-1.5 rounded-md bg-red-950/30 border border-red-800/30 text-red-400 hover:bg-red-900/40 transition-all"
                      title="Delete session"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Playback Panel ── */}
      {playbackSession && (
        <div
          className="rounded-xl border border-[#1E3A5F]/70 bg-[#0B1628]/90 backdrop-blur-sm overflow-hidden"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(249,115,22,0.06)' }}
        >
          <div className="h-[1px] bg-gradient-to-r from-transparent via-[#F97316]/40 to-transparent" />

          <div className="px-4 py-3 border-b border-[#1E3A5F]/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Play size={14} className="text-[#F97316]" />
              <span className="text-[13px] font-semibold text-zinc-200">Playback</span>
              <span className="text-[11px] font-mono text-zinc-500">{playbackSession.id}</span>
            </div>
            <button
              onClick={() => { setPlaybackSession(null); setIsPlaying(false); }}
              className="text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
            >
              ✕ Close
            </button>
          </div>

          <div className="px-4 py-4 space-y-4">
            {/* Timeline */}
            <PlaybackTimeline
              duration={playbackSession.duration}
              currentTime={playbackTime}
              markers={playbackSession.markers}
              onSeek={seekPlayback}
            />

            {/* Playback controls */}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => seekPlayback(0)}
                className="p-2 rounded-lg bg-[#0F1E35] border border-[#1E3A5F]/60 text-[#2D5A8E] hover:text-zinc-200 hover:border-[#F97316]/30 transition-all active:scale-95"
                title="Restart"
              >
                <SkipBack size={15} />
              </button>
              <button
                onClick={() => skipToMarker('prev')}
                disabled={playbackSession.markers.length === 0}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#0F1E35] border border-[#1E3A5F]/60 text-[#2D5A8E] hover:text-amber-400 hover:border-amber-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 text-[11px]"
                title="Previous marker"
              >
                <Bookmark size={11} />
                <span>Prev</span>
              </button>
              <button
                onClick={() => setIsPlaying((v) => !v)}
                className="flex items-center justify-center w-10 h-10 rounded-full transition-all active:scale-95"
                style={{
                  background: 'linear-gradient(135deg, #F97316, #FF9A4D)',
                  boxShadow: '0 0 16px rgba(249,115,22,0.4)',
                }}
              >
                {isPlaying
                  ? <Pause size={16} className="text-white" />
                  : <Play size={16} className="text-white ml-0.5" />
                }
              </button>
              <button
                onClick={() => skipToMarker('next')}
                disabled={playbackSession.markers.length === 0}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#0F1E35] border border-[#1E3A5F]/60 text-[#2D5A8E] hover:text-amber-400 hover:border-amber-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 text-[11px]"
                title="Next marker"
              >
                <span>Next</span>
                <Bookmark size={11} />
              </button>
              <button
                onClick={() => seekPlayback(playbackSession.duration)}
                className="p-2 rounded-lg bg-[#0F1E35] border border-[#1E3A5F]/60 text-[#2D5A8E] hover:text-zinc-200 hover:border-[#F97316]/30 transition-all active:scale-95"
                title="Jump to end"
              >
                <SkipForward size={15} />
              </button>
            </div>

            {/* Active marker callout */}
            {activeMarker && (
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{
                  background: 'rgba(251,191,36,0.08)',
                  border: '1px solid rgba(251,191,36,0.3)',
                  boxShadow: '0 0 12px rgba(251,191,36,0.1)',
                }}
              >
                <Bookmark size={13} className="text-amber-400 shrink-0" />
                <div>
                  <span className="text-[12px] font-semibold text-amber-300">{activeMarker.label}</span>
                  <span className="text-[11px] text-amber-600 ml-2 font-mono">{formatTime(activeMarker.time)}</span>
                </div>
              </div>
            )}

            {/* Archived metrics at current playback position */}
            {playbackMetrics && (
              <div>
                <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Zap size={10} className="text-[#F97316]" />
                  Archived Cognitive Metrics
                </p>
                <div className="flex flex-wrap gap-2">
                  <MetricPill label="Cog Load" value={playbackMetrics.cognitiveLoad} unit="/100" color="#F97316" />
                  <MetricPill label="Attention" value={playbackMetrics.attentionScore} unit="/100" color="#34d399" />
                  <MetricPill label="Beta" value={playbackMetrics.betaPower} unit="µV²" color="#60a5fa" />
                  <MetricPill label="Alpha" value={playbackMetrics.alphaPower} unit="µV²" color="#a78bfa" />
                  <MetricPill label="Theta" value={playbackMetrics.thetaPower} unit="µV²" color="#f59e0b" />
                  <MetricPill label="θ/α" value={playbackMetrics.thetaAlphaRatio} unit="" color="#fb7185" />
                  <MetricPill label="Coherence" value={playbackMetrics.interHemisphericCoherence} unit="%" color="#2dd4bf" />
                </div>
              </div>
            )}

            {/* AI Insight */}
            <div className="rounded-lg border border-[#1E3A5F]/50 bg-[#060D1A]/60 overflow-hidden">
              <div className="px-3 py-2 border-b border-[#1E3A5F]/40 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Brain size={13} className="text-[#F97316]" />
                  <span className="text-[11px] font-semibold text-zinc-300">AI Session Insight</span>
                </div>
                <button
                  onClick={() => generateInsight(playbackSession)}
                  disabled={loadingInsight}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] bg-[#F97316]/10 border border-[#F97316]/20 text-[#F97316] hover:bg-[#F97316]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Target size={10} />
                  {loadingInsight ? 'Analyzing...' : 'Analyze'}
                </button>
              </div>
              <div className="px-3 py-3">
                {loadingInsight ? (
                  <div className="flex items-center gap-2">
                    <div className="flex items-end gap-[2px] h-4">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="w-[3px] rounded-full bg-[#F97316]"
                          style={{ animation: `typingBar 1.1s ease-in-out ${i * 0.12}s infinite`, height: '100%' }}
                        />
                      ))}
                    </div>
                    <span className="text-[11px] text-zinc-500">Generating insight...</span>
                  </div>
                ) : aiInsight ? (
                  <p className="text-[12px] text-zinc-300 leading-relaxed">{aiInsight}</p>
                ) : (
                  <p className="text-[11px] text-zinc-600 italic">Click &quot;Analyze&quot; to generate AI insights for this archived session.</p>
                )}
              </div>
            </div>

            {/* Marker list for this session */}
            {playbackSession.markers.length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Bookmark size={10} className="text-amber-400" />
                  Timestamp Markers
                </p>
                <div className="flex flex-wrap gap-2">
                  {playbackSession.markers.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => seekPlayback(m.time)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] transition-all hover:scale-105 active:scale-95"
                      style={{
                        background: 'rgba(251,191,36,0.08)',
                        border: '1px solid rgba(251,191,36,0.25)',
                        color: '#fbbf24',
                      }}
                    >
                      <Bookmark size={10} />
                      <span className="font-medium">{m.label}</span>
                      <span className="font-mono text-amber-600">{formatTime(m.time)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes typingBar {
          0%, 100% { transform: scaleY(0.4); opacity: 0.5; }
          50% { transform: scaleY(1.2); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
